/**
 * Tokyo Metro — simplified game map.
 *
 * The real Tokyo subway has 12 lines and hundreds of stations. For a
 * playable app-sized v1 we use a curated 20-station grid:
 *   - 12 canonical line letters (G/M/H/T/C/Y/Z/N/A/I/S/E), matching
 *     the physical game and rulebook action-card column pairs.
 *   - 20 iconic Tokyo stations, laid out on a 6×6 grid so meeples
 *     can move along adjacency edges.
 *   - Each line has a station route (ordered list) — trains move
 *     along their own route in sequence. A station can sit on
 *     multiple lines (multi-circle station in the rulebook).
 *
 * This isn't geographic truth — it's a game board that reads well
 * on a phone and produces interesting economic choices. Swap in a
 * richer map later by extending LINES / STATIONS / LINE_ROUTES.
 */

export type LineId = 'G' | 'M' | 'H' | 'T' | 'C' | 'Y' | 'Z' | 'N' | 'A' | 'I' | 'S' | 'E'
export type StationId = string

export interface LineDef {
  id: LineId
  name: string
  /** Official Tokyo Metro / Toei line color. */
  color: string
  /**
   * Letter-shortcode pairing for action-card columns. The rulebook
   * groups the 12 lines into 6 column pairs (G/M, H/T, ...).
   */
  column: 0 | 1 | 2 | 3 | 4 | 5
}

export interface StationDef {
  id: StationId
  name: string
  /** Grid row 0–5 — used for adjacency + meeple movement v1. */
  row: number
  /** Grid col 0–5 — used for adjacency + meeple movement v1. */
  col: number
  /**
   * Approximate x position on the real Metro-Map.svg viewBox, as a
   * percentage 0–100. Eyeballed from rough Tokyo geography until we
   * transcribe the SVG's path data fully.
   */
  mapX: number
  /** Approximate y position on the real map, as a percentage 0–100. */
  mapY: number
  /** All lines that call at this station. */
  lines: LineId[]
}

export const LINES: LineDef[] = [
  { id: 'G', name: 'Ginza',         color: '#F59B1F', column: 0 },
  { id: 'M', name: 'Marunouchi',    color: '#E60012', column: 0 },
  { id: 'H', name: 'Hibiya',        color: '#9CAAB7', column: 1 },
  { id: 'T', name: 'Tozai',         color: '#00A0DE', column: 1 },
  { id: 'C', name: 'Chiyoda',       color: '#00A262', column: 2 },
  { id: 'Y', name: 'Yurakucho',     color: '#D5A12B', column: 2 },
  { id: 'Z', name: 'Hanzomon',      color: '#8F76CF', column: 3 },
  { id: 'N', name: 'Namboku',       color: '#00AC9B', column: 3 },
  { id: 'A', name: 'Asakusa',       color: '#E85298', column: 4 },
  { id: 'I', name: 'Mita',          color: '#006AB6', column: 4 },
  { id: 'S', name: 'Shinjuku',      color: '#B0C124', column: 5 },
  { id: 'E', name: 'Oedo',          color: '#B6007A', column: 5 },
]

/** Fast lookup from letter → LineDef. */
export const LINE_BY_ID: Record<LineId, LineDef> = LINES.reduce(
  (acc, l) => { acc[l.id] = l; return acc },
  {} as Record<LineId, LineDef>,
)

/**
 * The 20 playable stations. Rows/cols fit a 6×6 grid; empty cells
 * exist on purpose so the map has white space (real maps do too).
 */
export const STATIONS: StationDef[] = [
  { id: 'ikebukuro',       name: 'Ikebukuro',     row: 0, col: 0, mapX: 15, mapY: 22, lines: ['M', 'Y'] },
  { id: 'iidabashi',       name: 'Iidabashi',     row: 1, col: 1, mapX: 35, mapY: 30, lines: ['T', 'Y', 'N', 'E'] },
  { id: 'kagurazaka',      name: 'Kagurazaka',    row: 1, col: 2, mapX: 28, mapY: 26, lines: ['T'] },
  { id: 'ochanomizu',      name: 'Ochanomizu',    row: 1, col: 3, mapX: 47, mapY: 42, lines: ['M'] },
  { id: 'akihabara',       name: 'Akihabara',     row: 1, col: 4, mapX: 56, mapY: 42, lines: ['H'] },
  { id: 'asakusa',         name: 'Asakusa',       row: 1, col: 5, mapX: 78, mapY: 30, lines: ['A'] },
  { id: 'shinjuku',        name: 'Shinjuku',      row: 2, col: 0, mapX: 10, mapY: 50, lines: ['M', 'S', 'E'] },
  { id: 'yotsuya',         name: 'Yotsuya',       row: 2, col: 1, mapX: 28, mapY: 50, lines: ['M', 'N'] },
  { id: 'otemachi',        name: 'Otemachi',      row: 2, col: 2, mapX: 50, mapY: 55, lines: ['M', 'T', 'C', 'Z', 'I'] },
  { id: 'kanda',           name: 'Kanda',         row: 2, col: 4, mapX: 56, mapY: 50, lines: ['G'] },
  { id: 'ueno',            name: 'Ueno',          row: 2, col: 5, mapX: 70, mapY: 35, lines: ['G', 'H'] },
  { id: 'meiji_jingumae',  name: 'Meiji-jingumae', row: 3, col: 1, mapX: 20, mapY: 65, lines: ['C', 'Z'] },
  { id: 'nihombashi',      name: 'Nihombashi',    row: 3, col: 2, mapX: 53, mapY: 60, lines: ['G', 'T', 'A'] },
  { id: 'hibiya',          name: 'Hibiya',        row: 3, col: 3, mapX: 45, mapY: 65, lines: ['H', 'C', 'I'] },
  { id: 'shibuya',         name: 'Shibuya',       row: 4, col: 0, mapX: 15, mapY: 75, lines: ['G', 'Z'] },
  { id: 'roppongi',        name: 'Roppongi',      row: 4, col: 1, mapX: 28, mapY: 72, lines: ['H', 'E'] },
  { id: 'kasumigaseki',    name: 'Kasumigaseki',  row: 4, col: 2, mapX: 40, mapY: 70, lines: ['M', 'H', 'C'] },
  { id: 'ginza',           name: 'Ginza',         row: 4, col: 3, mapX: 55, mapY: 70, lines: ['G', 'M', 'H'] },
  { id: 'shimbashi',       name: 'Shimbashi',     row: 4, col: 4, mapX: 60, mapY: 78, lines: ['G', 'A'] },
  { id: 'tokyo_st',        name: 'Tokyo',         row: 3, col: 5, mapX: 58, mapY: 60, lines: ['M'] },
]

/** Fast lookup from station id → StationDef. */
export const STATION_BY_ID: Record<StationId, StationDef> = STATIONS.reduce(
  (acc, s) => { acc[s.id] = s; return acc },
  {} as Record<StationId, StationDef>,
)

/**
 * Per-line station route (ordered). Trains start at [0] and run
 * through in sequence, then reverse for the return trip (standard
 * Tokyo subway terminus-to-terminus behavior).
 *
 * The rulebook says "active trains move 5 spaces per train phase,
 * advancing to the next highest like-colored station on the map" —
 * here `spaces` means index steps along this route.
 */
export const LINE_ROUTES: Record<LineId, StationId[]> = {
  G: ['shibuya', 'ginza', 'shimbashi', 'nihombashi', 'kanda', 'ueno'],
  M: ['ikebukuro', 'ochanomizu', 'otemachi', 'kasumigaseki', 'yotsuya', 'shinjuku', 'tokyo_st'],
  H: ['ueno', 'akihabara', 'hibiya', 'ginza', 'kasumigaseki', 'roppongi'],
  T: ['kagurazaka', 'iidabashi', 'otemachi', 'nihombashi'],
  C: ['meiji_jingumae', 'otemachi', 'hibiya', 'kasumigaseki'],
  Y: ['ikebukuro', 'iidabashi'],
  Z: ['shibuya', 'meiji_jingumae', 'otemachi'],
  N: ['iidabashi', 'yotsuya'],
  A: ['asakusa', 'nihombashi', 'shimbashi'],
  I: ['otemachi', 'hibiya'],
  S: ['shinjuku'],
  E: ['shinjuku', 'iidabashi', 'roppongi'],
}

/**
 * 4-direction grid adjacency — meeples can step to orthogonal
 * neighbors on the 6×6 grid IF those cells contain a station.
 * Computed once at module load.
 */
export const STATION_ADJACENCY: Record<StationId, StationId[]> = (() => {
  const out: Record<StationId, StationId[]> = {}
  const byCell = new Map<string, StationDef>()
  for (const s of STATIONS) byCell.set(`${s.row},${s.col}`, s)
  for (const s of STATIONS) {
    const neighbors: StationId[] = []
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
      const other = byCell.get(`${s.row + dr},${s.col + dc}`)
      if (other) neighbors.push(other.id)
    }
    out[s.id] = neighbors
  }
  return out
})()

export const GRID_ROWS = 6
export const GRID_COLS = 6
